// frontend/src/pages/especialistas/EspecialistasListPage.jsx
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../../services/api';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import PermissionDeniedAlert from '../../components/common/PermissionDeniedAlert';
import AlertException from '../../components/common/AlertException';
import AppSelect from '../../components/common/AppSelect';
import { useAuth } from '../../contexts/AuthContext';

const SEARCH_DEBOUNCE_MS = 450;

function EspecialistasListPage() {
  const { loading: authLoading, hasPermission } = useAuth();

  const canManage = hasPermission('config.especialistas');
  const canView   = hasPermission('especialistas.view') || canManage;
  const canCreate = hasPermission('especialistas.create') || canManage;
  const canEdit   = hasPermission('especialistas.edit') || canManage;
  const canDelete = hasPermission('especialistas.delete') || canManage;

  const [especialistas, setEspecialistas] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('active');
  const [paginationInfo, setPaginationInfo] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const abortRef = useRef(null);
  const perPageOptions = [
    { value: 20, label: '20' },
    { value: 50, label: '50' },
    { value: 'all', label: 'Todos' },
  ];

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm.trim());
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    if (authLoading) return;

    const controller = new AbortController();
    abortRef.current = controller;

    const fetchEspecialistas = async () => {
      setLoading(true);
      setError(null);

      if (!canView) {
        setError({ kind: 'permission' });
        setLoading(false);
        return;
      }

      try {
        const response = await api.get('/especialistas', {
          signal: controller.signal,
          params: {
            page: currentPage,
            per_page: perPage === 'all' ? 10000 : Number(perPage),
            status: statusFilter,
            search: debouncedSearch || undefined,
          },
        });
        setEspecialistas(response?.data?.data ?? []);
        setPaginationInfo(response?.data?.meta ?? null);
      } catch (err) {
        if (controller.signal.aborted) return;
        if (err?.response?.status === 403) {
          setError({ kind: 'permission' });
        } else {
          setError(err);
        }
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };

    fetchEspecialistas();
    return () => controller.abort();
  }, [authLoading, canView, currentPage, debouncedSearch, perPage, statusFilter]);

  const handlePageChange = (newPage) => {
    if (!paginationInfo) return;
    if (newPage < 1 || newPage > paginationInfo.last_page) return;
    setCurrentPage(newPage);
  };

  const totalRegistrosPaginacao = Number.isFinite(Number(paginationInfo?.total))
    ? Number(paginationInfo.total)
    : especialistas.length;

  const handlePerPageChange = (value) => {
    setPerPage(value);
    setCurrentPage(1);
  };

  const handleDelete = async (id) => {
    if (!canDelete) return;

    if (window.confirm('Tem certeza de que deseja desativar este especialista?')) {
      try {
        await api.delete(`/especialistas/${id}`);
        setEspecialistas((prev) => prev.filter((e) => e.id !== id));
      } catch {
        alert('Ocorreu um erro ao desativar o especialista.');
      }
    }
  };

  const handleRestore = async (id) => {
    if (!canDelete) return;

    if (window.confirm('Tem certeza de que deseja reativar este especialista?')) {
      try {
        await api.patch(`/especialistas/${id}/restore`);
        setEspecialistas((prev) => prev.filter((e) => e.id !== id));
      } catch {
        alert('Ocorreu um erro ao reativar o especialista.');
      }
    }
  };

  if (authLoading) {
    return (
      <div className="especialistas-page">
        <LoadingSpinner text="Verificando sessão..." />
      </div>
    );
  }

  if (loading) {
    return (
      <div className="especialistas-page">
        <LoadingSpinner text="Carregando especialistas..." />
      </div>
    );
  }

  if (error) {
    if (error.kind === 'permission') {
      return (
        <div className="especialistas-page">
          <PermissionDeniedAlert
            requiredPermission="especialistas.view"
            action="visualizar a lista de especialistas"
          />
        </div>
      );
    }
    return (
      <div className="especialistas-page">
        <AlertException error={error} fallback="Não foi possível carregar os especialistas." />
      </div>
    );
  }

  return (
    <div className="especialistas-page">
      <div className="content-header">
        <h1 className="content-title">Especialistas</h1>
        <p className="content-subtitle">Gerenciamento de especialistas socioeducativos</p>
      </div>

      <div className="card especialistas-list-card">
        <div className="card-body">
          <div className="list-toolbar especialistas-list-toolbar d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
            <div className="especialistas-filters-group d-flex align-items-center gap-3">
              <div className="input-group especialistas-search-group">
                <span className="input-group-text"><i className="bi bi-search"></i></span>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Buscar por nome..."
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    if (currentPage !== 1) {
                      setCurrentPage(1);
                    }
                  }}
                />
              </div>
              <div className="especialistas-status-filter d-flex align-items-center gap-2">
                <label htmlFor="especialistasStatusFilter" className="mb-0 especialistas-status-label">Filtrar por:</label>
                <select
                  id="especialistasStatusFilter"
                  className="form-select"
                  value={statusFilter}
                  onChange={(e) => {
                    setStatusFilter(e.target.value);
                    setCurrentPage(1);
                  }}
                  aria-label="Filtrar especialistas por status"
                >
                  <option value="active">Ativos</option>
                  <option value="inactive">Inativos</option>
                </select>
              </div>
              <div className="d-flex align-items-center gap-2">
                <span className="text-secondary small">Registros por página</span>
                <AppSelect
                  className="especialistas-per-page-select"
                  ariaLabel="Registros por página"
                  options={perPageOptions}
                  value={perPage}
                  onChange={handlePerPageChange}
                />
              </div>
            </div>

            {canCreate && (
              <Link to="/painel/especialistas/novo" className="btn btn-primary">
                <i className="bi bi-plus-lg me-2"></i>
                Novo Especialista
              </Link>
            )}
          </div>

          <div className="table-responsive">
            <table className="table table-dark-custom table-stack">
              <thead>
                <tr>
                  <th>Nome</th>
                  <th>Status</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {especialistas.length > 0 ? (
                  especialistas.map((e) => {
                    const isInactive = Boolean(e.deleted_at);

                    return (
                      <tr key={e.id}>
                        <td data-label="Nome">{e.nome}</td>
                        <td data-label="Status">{isInactive ? 'Inativo' : 'Ativo'}</td>
                        <td className="table-actions especialistas-table-actions d-flex gap-2" data-label="Ações">
                          {canEdit && !isInactive && (
                            <Link
                              to={`/painel/especialistas/${e.id}/editar`}
                              className="btn btn-sm btn-outline-warning"
                              title="Editar"
                            >
                              <i className="bi bi-pencil"></i>
                            </Link>
                          )}

                          {canDelete && !isInactive && (
                            <button
                              className="btn btn-sm btn-outline-danger"
                              title="Desativar"
                              onClick={() => handleDelete(e.id)}
                            >
                              <i className="bi bi-trash"></i>
                            </button>
                          )}

                          {canDelete && isInactive && (
                            <button
                              className="btn btn-sm btn-outline-success"
                              title="Reativar"
                              onClick={() => handleRestore(e.id)}
                            >
                              <i className="bi bi-person-check"></i>
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="3" className="text-center">Nenhum especialista encontrado.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {paginationInfo && paginationInfo.last_page > 1 && perPage !== 'all' && (
            <div className="d-flex justify-content-between align-items-center mt-3">
              <span className="especialistas-pagination-summary">
                Página <strong>{paginationInfo.current_page}</strong> de{' '}
                <strong>{paginationInfo.last_page}</strong> -{' '}
                <strong>{totalRegistrosPaginacao.toLocaleString('pt-BR')}</strong> registros
              </span>
              <div className="btn-group">
                <button
                  className="btn btn-outline-secondary"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  <i className="bi bi-chevron-left"></i> Anterior
                </button>
                <button
                  className="btn btn-outline-secondary"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === paginationInfo.last_page}
                >
                  Próximo <i className="bi bi-chevron-right"></i>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default EspecialistasListPage;
